import React, { useCallback } from 'react';
import { Download, Table, FileSpreadsheet, FileBarChart, Plus, ChevronDown } from "lucide-react";
import style from "../../style.module.css";
import FiltersSelect from "../FiltersSelect/FiltersSelect";
import ActivityTypeFilter from "../ActivityTypeFilter/ActivityTypeFilter";
import ActivityTagsFilter from "../ActivityTagsFilter/ActivityTagsFilter";
import DateRangeFilter from "../DateRangeFilter/DateRangeFilter";
import { ViewSwitch } from "../ViewSwitch/ViewSwitch";
import { usePdfExport } from "../PdfExport/usePdfExport";
import { useExcelExport } from "../ExcelExport/useExcelExport";
import { activityStore } from "../../../../../store/activity.store";
import { Interval } from "../../../../../model/activity.model";
import { ActivityStatus } from "../../../../../enum/activity.enum";
import { Button } from "../../../../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../ui/alert-dialog";
import { useIntl } from "react-intl";

interface RecordsActionsProps {
  view: string;
  setView: (view: string) => void;
}

export const RecordsActions: React.FC<RecordsActionsProps> = ({ view, setView }) => {
  const { formatMessage } = useIntl();
  const receiveExport = activityStore(state => state.receiveExport);
  const daysIntervals = activityStore(state => state.daysIntervals || []);
  const receiveExportLoading = activityStore(state => state.loading.receiveExport);
  const setActivityToCreate = activityStore(state => state.setActivityToCreate);
  const { exportPdf, isExporting: isPdfExporting, alert: pdfAlert, closeAlert: closePdfAlert } = usePdfExport();
  const { exportExcel, isExporting: isExcelExporting, alert: excelAlert, closeAlert: closeExcelAlert } = useExcelExport();

  const isDataExist = !!daysIntervals?.length;

  const openModalToForCreateActivity = useCallback(() => {
    const interval = { start: new Date().toISOString(), finish: new Date().toISOString() } as Interval;
    const newActivity = {
      status: ActivityStatus.STOPPED,
      start: '',
      intervals: [ interval ],
      tags: [],
      typeId: ''
    };
    setActivityToCreate(newActivity);
  }, [setActivityToCreate]);

  return <>
    <div className={style.RecordsPageActions}>
      <ActivityTypeFilter />
      <ActivityTagsFilter />
      <FiltersSelect />
    </div>
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2">
      <div className="flex justify-center sm:justify-start">
        <ViewSwitch view={view} setView={setView} />
      </div>
      <div className="flex justify-center sm:justify-start">
        <DateRangeFilter />
      </div>
      <div className="hidden sm:flex sm:flex-1" />
      <div className="flex justify-center gap-2 sm:justify-end">
        <Button variant="segment" onClick={openModalToForCreateActivity}>
          <Plus className="h-4 w-4" />
          {formatMessage({id: 'ADD_RECORD'})}
        </Button>
        {isDataExist && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="segment" loading={isPdfExporting || isExcelExporting || receiveExportLoading} title={formatMessage({id: 'EXPORT'})}>
                <Download className="h-4 w-4" />
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={receiveExport}>
                <Table className="mr-2 h-4 w-4" />
                CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportExcel}>
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportPdf}>
                <FileBarChart className="mr-2 h-4 w-4" />
                PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>

    <AlertDialog open={pdfAlert.open} onOpenChange={(open) => !open && closePdfAlert()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{pdfAlert.title}</AlertDialogTitle>
          <AlertDialogDescription>{pdfAlert.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={closePdfAlert}>OK</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog open={excelAlert.open} onOpenChange={(open) => !open && closeExcelAlert()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{excelAlert.title}</AlertDialogTitle>
          <AlertDialogDescription>{excelAlert.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={closeExcelAlert}>OK</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
}
